import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, Lightning, X, Sparkle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Rough per-event equivalent value of the bundled Pro features that Studio Pass auto-unlocks:
// Pro upgrade (£19) + Pocket Booth (£29) + Guestbook (£15) + Priority render (£9) ≈ £72.
// Conservative: use £35 so the math feels real, not salesy.
const PER_EVENT_VALUE_PENCE = 3500;

const StudioPassBanner = ({ events = [] }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ active: false });
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem('studioPassUpsellDismissed') === '1'
  );

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    axios
      .get(`${BACKEND_URL}/api/studio-pass/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setStatus(r.data || { active: false }))
      .catch(() => {});
  }, []);

  // Events auto-unlocked by Studio Pass (created while active subscription held)
  const autoUnlockedCount = useMemo(
    () => events.filter((e) => e.studio_pass_event === true).length,
    [events]
  );

  // Savings: unlocked events × per-event value
  const savedPence = autoUnlockedCount * PER_EVENT_VALUE_PENCE;
  const savedStr = savedPence > 0 ? `£${(savedPence / 100).toFixed(savedPence % 100 === 0 ? 0 : 2)}` : null;

  // ---------- PAYMENT-FAILED / DUNNING STATE ----------
  // When Stripe reports an unpaid invoice, we pause Pro access server-side
  // (see `has_studio_pass`) — surface that loudly with a red-orange banner
  // and a one-click Reactivate button that opens their Stripe Portal.
  if (status.status === 'past_due' || status.status === 'unpaid') {
    const failedAgo = status.payment_failed_at
      ? new Date(status.payment_failed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : null;
    const planLabel = status.plan === 'annual' ? 'Annual' : 'Monthly';

    const openPortal = async () => {
      const token = localStorage.getItem('session_token');
      try {
        const r = await axios.post(
          `${BACKEND_URL}/api/studio-pass/portal`,
          { origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (r.data?.url) window.location.href = r.data.url;
      } catch (_) {}
    };

    return (
      <div
        className="mb-8 rounded-2xl p-5 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(239,68,68,0.22), rgba(255,138,0,0.12))',
          border: '1px solid rgba(239,68,68,0.55)',
          animation: 'studioPassAlertPulse 2.4s ease-in-out infinite',
        }}
        data-testid="studio-pass-past-due-banner"
      >
        <style>{`
          @keyframes studioPassAlertPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.35); }
            50%      { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
          }
        `}</style>
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse"
            style={{ background: 'linear-gradient(135deg, #EF4444, #FF8A00)' }}
          >
            <Lightning size={22} weight="fill" className="text-white" />
          </div>

          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white text-lg">Studio Pass · {planLabel}</span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider"
                style={{ background: 'rgba(239,68,68,0.2)', color: '#FCA5A5' }}
              >
                Payment failed · Access paused
              </span>
            </div>
            <p className="text-sm text-gray-200 mt-1">
              Your card couldn't be charged{failedAgo ? ` on ${failedAgo}` : ''} — Pro features (no watermark, Photobooth, Pocket Booth, Video Guestbook, priority render) are <span className="text-white font-semibold">paused</span> across your events.
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              Update your payment method to restore access instantly. Your events and data are untouched.
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                onClick={openPortal}
                className="inline-flex items-center gap-2 px-4 h-10 rounded-xl font-bold text-white text-sm transition-all active:scale-[0.97]"
                style={{ background: 'linear-gradient(135deg, #EF4444, #FF8A00)' }}
                data-testid="studio-pass-reactivate"
              >
                <Lightning size={14} weight="bold" /> Reactivate Studio Pass
              </button>
              <button
                type="button"
                onClick={openPortal}
                className="inline-flex items-center gap-2 px-3 h-10 rounded-xl font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 text-xs transition-colors"
                data-testid="studio-pass-past-due-portal"
              >
                Manage billing →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- SUBSCRIBER STATE ----------
  if (status.active) {
    const renewsOn = status.current_period_end
      ? new Date(status.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : null;
    const planLabel = status.plan === 'annual' ? 'Annual' : 'Monthly';

    return (
      <div
        className="mb-8 rounded-2xl p-5 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(184,151,98,0.18), rgba(194,157,82,0.10))',
          border: '1px solid rgba(184,151,98,0.45)',
        }}
        data-testid="studio-pass-active-banner"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <Crown size={22} weight="fill" className="text-white" />
          </div>

          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white text-lg">Studio Pass · {planLabel}</span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ background: 'rgba(34,197,94,0.15)', color: '#4ADE80' }}
              >
                Active
              </span>
              {status.cancel_at_period_end ? (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">· Cancels at period end</span>
              ) : null}
            </div>
            <p className="text-sm text-gray-300 mt-1">
              Every new event you create gets <span className="text-white font-semibold">Pro features auto-unlocked</span> — no watermarks, priority render, unlimited template reuse.
            </p>
            {renewsOn ? (
              <p className="text-[11px] text-gray-500 mt-1">
                {status.cancel_at_period_end ? 'Ends on ' : 'Renews on '}
                <span className="text-gray-300">{renewsOn}</span>
              </p>
            ) : null}
          </div>

          {autoUnlockedCount > 0 ? (
            <div
              className="text-right px-4 py-3 rounded-xl self-start"
              style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid="studio-pass-savings"
            >
              <div className="text-xs text-gray-400 font-semibold">
                {autoUnlockedCount} {autoUnlockedCount === 1 ? 'event' : 'events'} auto-unlocked
              </div>
              {savedStr ? (
                <div className="text-lg font-black text-white leading-tight">
                  {savedStr} <span className="text-xs font-semibold text-gray-400">saved vs pay-per-event</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex gap-4 mt-4 items-center flex-wrap">
          {/* Auto-renew toggle */}
          {status.subscription_id && (
            <button
              type="button"
              onClick={async () => {
                const token = localStorage.getItem('session_token');
                const nextEnabled = !!status.cancel_at_period_end; // if it's currently cancelling, re-enable = true
                try {
                  const r = await axios.post(
                    `${BACKEND_URL}/api/studio-pass/auto-renew`,
                    { enabled: nextEnabled },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setStatus((s) => ({ ...s, cancel_at_period_end: r.data?.cancel_at_period_end }));
                } catch (_) {}
              }}
              className="inline-flex items-center gap-2 text-xs font-semibold transition-colors"
              data-testid="studio-pass-autorenew-toggle"
              aria-pressed={!status.cancel_at_period_end}
              title="Toggle auto-renew"
            >
              <span
                className="relative w-8 h-4 rounded-full transition-colors"
                style={{ background: !status.cancel_at_period_end ? '#4ADE80' : 'rgba(255,255,255,0.15)' }}
              >
                <span
                  className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
                  style={{ left: !status.cancel_at_period_end ? '16px' : '2px' }}
                />
              </span>
              <span className={!status.cancel_at_period_end ? 'text-green-400' : 'text-gray-400'}>
                Auto-renew {!status.cancel_at_period_end ? 'on' : 'off'}
              </span>
            </button>
          )}

          <button
            onClick={async () => {
              const token = localStorage.getItem('session_token');
              try {
                const r = await axios.post(
                  `${BACKEND_URL}/api/studio-pass/portal`,
                  { origin_url: window.location.origin },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (r.data?.url) window.location.href = r.data.url;
              } catch (_) {}
            }}
            className="text-xs text-gray-400 hover:text-white transition-colors"
            data-testid="studio-pass-manage"
          >
            Manage billing →
          </button>
        </div>
      </div>
    );
  }

  // ---------- UPSELL STATE ----------
  // Show upsell banner once user has created 2+ events and hasn't dismissed it.
  if (dismissed || events.length < 2) return null;

  return (
    <div
      className="mb-8 rounded-2xl p-5 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(194,157,82,0.10), rgba(184,151,98,0.10))',
        border: '1px solid rgba(194,157,82,0.25)',
      }}
      data-testid="studio-pass-upsell-banner"
    >
      <button
        onClick={() => {
          window.localStorage.setItem('studioPassUpsellDismissed', '1');
          setDismissed(true);
        }}
        className="absolute top-3 right-3 p-1 rounded-md text-gray-500 hover:text-white hover:bg-white/5"
        aria-label="Dismiss"
        data-testid="studio-pass-upsell-dismiss"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          <Lightning size={20} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2">
            <span className="font-bold text-white">Running multiple events?</span>
            <Sparkle size={14} weight="fill" className="text-[#C29D52]" />
          </div>
          <p className="text-sm text-gray-300 mt-1">
            You've hosted <span className="text-white font-semibold">{events.length} events</span>. Studio Pass pays for itself at just 2 events a year — unlimited events, no watermarks, priority render.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/studio-pass')}
            className="px-4 h-10 rounded-xl font-bold text-white text-sm transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="studio-pass-upsell-cta"
          >
            Upgrade — £299/yr
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudioPassBanner;
